import { isOnline } from "./isOnline";
import { isOfflineFeatureEnabled } from "./featureFlag";

/**
 * Blocks writes that have no offline story, at the transport layer.
 *
 * The alternative was disabling every write control on every Tier 2 page one by
 * one — dozens of edits, and any page missed would still fire a doomed request
 * and surface a raw network error. Guarding both HTTP transports covers the whole
 * app at once and cannot be forgotten on a new page.
 *
 * Tier 1 writes never reach here while offline: their sagas check connectivity
 * first and enqueue instead of calling the API (see routesSaga, visitsSaga,
 * householdsSaga). So by the time a write reaches a transport with no
 * connectivity, it is by definition one we cannot honour.
 */
export class OfflineWriteBlockedError extends Error {
  /**
   * Deliberately shaped like a network failure — no `response` property. The sync
   * engine classifies a missing response as "connection died, stop and wait"
   * rather than "this row is bad, set it aside", which is the correct reading if
   * the connection drops mid-flush.
   */
  constructor() {
    super("offline-write-blocked");
    this.name = "OfflineWriteBlockedError";
  }
}

const WRITE_METHODS = ["post", "put", "patch", "delete"];

export const isWriteMethod = (method?: string): boolean =>
  WRITE_METHODS.includes(String(method ?? "get").toLowerCase());

/** True when this request must not be attempted right now. */
export const shouldBlockWrite = (method?: string): boolean =>
  // Gated on the feature flag too: for a user who is not opted in, an offline write
  // should fail the way it always has rather than be intercepted by new machinery.
  isWriteMethod(method) && !isOnline() && isOfflineFeatureEnabled();
